import React, { useState } from "react";
import { IDoctor, ITreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";

interface Props {
  doctors: IDoctor[];
  treatments: ITreatment[]; // Needed to display assigned treatment names
  onDoctorsChange: (doctors: IDoctor[]) => void;
}

// Single Doctor Card — treatment assignment is managed in the Treatments step.
const DoctorCard: React.FC<{
  doctor: IDoctor;
  allTreatments: ITreatment[];
}> = ({ doctor, allTreatments }) => {
  const getAssignedTreatments = () => {
    return doctor?.assignedTreatmentIds
      ?.map((id) => {
        const s = String(id);
        const t = allTreatments.find(
          (x) => String(x.id) === s || String(x.treatmentId ?? "") === s
        );
        return t?.name;
      })
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg shadow-slate-200/50 flex flex-col">
      <img
        src={doctor.image}
        alt={doctor.firstName}
        className="w-full h-[150px] object-cover rounded-t-lg"
      />
      <div className="p-3 flex-grow">
        <h4 className="text-sm font-semibold text-slate-800 mb-1.5">
          Dr. {doctor.firstName + " " + doctor.lastName}
        </h4>
        <div className="font-semibold text-xs text-primary mb-2">
          {doctor.speciality}
        </div>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Studies:</strong>
          {doctor.studies.join(", ")}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Languages:</strong>
          {doctor.languages.join(", ")}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Treatments:</strong>
          {getAssignedTreatments() || "None — assign in the Treatments step"}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">
            Dentist Available for Booking:
          </strong>
          {doctor.IsBookDentist ? "Yes" : "No"}
        </p>
      </div>
    </div>
  );
};

// Main Doctors Section
export const Doctors: React.FC<Props> = ({
  doctors,
  treatments,
  onDoctorsChange,
}) => {
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  const handleDelete = () => {
    if (!deleteCandidate) return;
    onDoctorsChange(doctors.filter((d) => d.id !== deleteCandidate));
    setDeleteCandidate(null);
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <div>
          <h2 className="text-xs font-bold text-primary mb-0">Doctors</h2>
          <p className="mt-1 text-[11px] text-slate-500">
            Which treatments each dentist provides is managed in the Treatments step.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {doctors.map((doc, i) => (
          <DoctorCard key={i} doctor={doc} allTreatments={treatments} />
        ))}
      </div>

      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Doctor"
          message={`Are you sure you want to delete Dr. ${
            doctors.find((d) => d.id === deleteCandidate)?.firstName
          }?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}
    </section>
  );
};
